import { Skeleton } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import { useGetInvoiceByIdQuery } from '../../requests/invoices.gql.generated';
import { CreateInvoiceStepIndex } from './CreateInvoiceHeaderWithTimelineStepper';
import { CreateInvoicePayment } from './CreateInvoicePayment';
import { CreateInvoiceReview } from './CreateInvoiceReview';
import { CreateMultiInvoiceComponents } from './CreateMultiInvoiceComponents';
import { CreateMultiInvoiceDetails } from './CreateMultiInvoiceDetails';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';

export const CreateInvoiceContainerBlank = () => {
  const [
    currentStepIndex,
    setCurrentStepIndex,
  ] = useState<CreateInvoiceStepIndex>(0);

  return (
    <>
      {currentStepIndex === 0 && (
        <CreateMultiInvoiceDetails setCurrentStepIndex={setCurrentStepIndex} />
      )}

      {currentStepIndex === 1 && (
        <CreateInvoicePayment setCurrentStepIndex={setCurrentStepIndex} />
      )}

      {currentStepIndex === 2 && (
        <CreateMultiInvoiceComponents
          setCurrentStepIndex={setCurrentStepIndex}
        />
      )}

      {currentStepIndex === 3 && (
        <CreateInvoiceReview setCurrentStepIndex={setCurrentStepIndex} />
      )}
    </>
  );
};

export const CreateInvoiceContainerWithPrefilledInvoice = ({
  prefillInvoiceId,
}: {
  prefillInvoiceId: string;
}) => {
  const { facilities } = useCreditFacilitiesOrThrow();
  const { prefillInvoice } = useCreateInvoiceContext();

  const [
    currentStepIndex,
    setCurrentStepIndex,
  ] = useState<CreateInvoiceStepIndex>(0);

  const {
    data: prefillInvoiceData,
    loading: isPrefillInvoiceDataLoading,
  } = useGetInvoiceByIdQuery({
    fetchPolicy: 'no-cache',
    variables: { invoiceId: prefillInvoiceId },
  });

  useEffect(() => {
    if (prefillInvoiceData?.getInvoiceById && !isPrefillInvoiceDataLoading) {
      const selectedCreditFacility = facilities.find(
        (facility) =>
          facility.id === prefillInvoiceData.getInvoiceById.creditFacilityId,
      );

      if (selectedCreditFacility) {
        prefillInvoice({
          prefillInvoiceData: prefillInvoiceData.getInvoiceById,
          prefillCreditFacility: selectedCreditFacility,
        });
      }
    }
  }, [
    prefillInvoiceData,
    isPrefillInvoiceDataLoading,
    facilities,
    prefillInvoice,
    prefillInvoiceId,
  ]);

  if (!prefillInvoiceData?.getInvoiceById || isPrefillInvoiceDataLoading) {
    return <Skeleton height="300px" />;
  }

  return (
    <>
      {currentStepIndex === 0 && (
        <CreateMultiInvoiceDetails setCurrentStepIndex={setCurrentStepIndex} />
      )}
      {currentStepIndex === 1 && (
        <CreateInvoicePayment setCurrentStepIndex={setCurrentStepIndex} />
      )}
      {currentStepIndex === 2 && (
        <CreateMultiInvoiceComponents
          setCurrentStepIndex={setCurrentStepIndex}
        />
      )}
      {currentStepIndex === 3 && (
        <CreateInvoiceReview setCurrentStepIndex={setCurrentStepIndex} />
      )}
    </>
  );
};

export const CreateInvoiceContainer = ({
  prefillInvoiceId,
}: {
  prefillInvoiceId?: string;
}) => {
  return prefillInvoiceId ? (
    <CreateInvoiceContainerWithPrefilledInvoice
      prefillInvoiceId={prefillInvoiceId}
    />
  ) : (
    <CreateInvoiceContainerBlank />
  );
};
